import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useTranslator } from '@picsart/rc/services/localization';
import flatMap from 'lodash/flatMap';
import ImageFrame from '@picsart/imageframe/ImageFrame';
import Image from '@picsart/image/Image';
import { Typography } from '@picsart/cascade';
import { Badge, BadgeSizeMD } from '@picsart/badge';
import Text from '@picsart/text/Text';
import { Placements, Tooltip } from '@picsart/tooltip';
import { ExpandStates } from '@picsart/editorlayout';

import { IShopBackgroundItem, IShopBackgrounds, IUserBackground } from 'types';
import useMemoSelector from 'hooks/useMemoSelector';
import { getUserData } from 'store/selectors/general';
import { getBatchActiveColor, getBatchActiveTexture } from 'store/batch/selectors';
import batchSlice from 'store/batch';
import checkIsPremium from 'utils/premium/checkIsPremium';
import { IProcessingTool } from 'pages/Batch/sections/Sidebar/types';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';
import { COMMERCIAL_LICENSE, PERSONAL_LICENSE } from 'configs/constants';
import useMount from 'hooks/common/useMount';
import ProBadge from 'components/common/ProBadge';
import { BATCH_TOOLS } from 'pages/Batch/configs';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';

import useStyles from '../styles';

import TextureCarousel from './TextureCarousel';
import TextureGrid from './TextureGrid';

type TextureProps = IProcessingTool & {
  backgrounds: IShopBackgrounds[];
}
function Texture({ inProgress, backgrounds }: TextureProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslator();
  const { isWeb, expandedState } = useBatchData();

  const activeTexture = useMemoSelector(getBatchActiveTexture);
  const activeColor = useMemoSelector(getBatchActiveColor);
  const { isSubscribed, waitingUserSubscription, isProFeatureEnabledForLocation } = useMemoSelector(getUserData);
  const showAsCarousel = !isWeb && expandedState !== ExpandStates.Full;

  const handleActiveBg = useCallback((currentBg: IUserBackground | null) => {
    dispatch(batchSlice.actions.setActiveTexture(currentBg));
    dispatch(batchSlice.actions.setActiveColor(null));
    analytics.utils().setSubTool(BATCH_TOOLS.removeBG, {
      name: SubToolNames.texture,
      value: currentBg?.id as number,
    }, SubToolNames.color);
  }, [dispatch]);

  const handleBackgroundClick = useCallback((currentBg: IUserBackground | null, position: number) => {
    const shopItem = backgrounds.find(({ items }) => items.some(item => item.id === currentBg?.id));

    analytics.textureClick('click', {
      position,
      ...(shopItem && {
        is_premium: checkIsPremium(shopItem),
        item_id: String((currentBg as IShopBackgroundItem).id),
        license: shopItem.commercial ? COMMERCIAL_LICENSE : PERSONAL_LICENSE,
      }),
    });
    handleActiveBg(currentBg);
  }, [backgrounds, handleActiveBg]);

  const renderBadge = () => {
    if (waitingUserSubscription) return null;

    if (!isProFeatureEnabledForLocation) {
      return (
        <Tooltip placement={Placements.Bottom} className={classes.tooltip} target={<Badge className={classes.badge} size={BadgeSizeMD} />}>
          <Text size={Typography.t3}>{translate('This is a premium texture')}</Text>
        </Tooltip>
      );
    }

    if (isSubscribed) return null;

    return <ProBadge className={classes.badge} />;
  };

  const textureItems = useMemo(
    () => flatMap(backgrounds, ({ type, items }) => items.map(item => ({
      ...item,
      type,
    }))),
    [backgrounds],
  );

  useMount(() => {
    analytics.textureOpened();
  });

  const Wrapper = showAsCarousel ? TextureCarousel : TextureGrid;

  return (
    <Wrapper>
      <ImageFrame
        isInteractive={!inProgress}
        onClick={() => handleBackgroundClick(null, 0)}
        isActive={!activeTexture && !activeColor}
        isTransparent
        width={showAsCarousel ? 60 : '100%'}
      />
      {textureItems.map((texture, idx) => (
        <ImageFrame
          isInteractive={!inProgress}
          onClick={() => handleBackgroundClick(texture, idx + 1)}
          isActive={activeTexture?.url === texture.url}
          key={texture.id}
          width={showAsCarousel ? 60 : '100%'}
          className={classNames({
            [classes.carouselTextureItem]: showAsCarousel,
          })}
        >
          {checkIsPremium(texture) && renderBadge()}
          <Image src={`${texture.preview_url}?to=fixed&r=120x120`} alt="img" />
        </ImageFrame>
      ))}
    </Wrapper>
  );
}

export default Texture;
